/**
 * Utility for generating unique citation identifier keys.
 * Used for deduplication, React keys, and scroll-to-citation features.
 */

/**
 * Get unique identifier key for a citation based on its source type.
 *
 * Key generation priority:
 * 1. source_id (unified backend identifier) - preferred when available
 * 2. Fallback logic for backwards compatibility:
 *    - user_document: uses document_id (unique across user's library)
 *    - attachment/image: uses file_key (unique S3 path) or falls back to file_name
 *    - web_search: uses url or title
 */
export function getCitationKey(citation: {
  source_id?: string;
  source_type?: string;
  document_id?: string;
  file_key?: string;
  file_name?: string;
  url?: string;
  title?: string;
}): string {
  // Use source_id when available (unified backend identifier)
  if (citation.source_id) {
    const prefix = citation.source_type || 'source';
    return `${prefix}:${citation.source_id}`;
  }

  // TODO: Remove fallback logic once all clients send source_id (EVI-517)
  // Fallback logic for backwards compatibility
  if (citation.source_type === 'user_document' && citation.document_id) {
    return `user_document:${citation.document_id}`;
  }
  // Use file_key for attachments and images (unique S3 path)
  if (
    (citation.source_type === 'attachment' || citation.source_type === 'image') &&
    citation.file_key
  ) {
    return `${citation.source_type}:${citation.file_key}`;
  }
  // Fallback to file_name for backwards compatibility
  if (
    (citation.source_type === 'attachment' || citation.source_type === 'image') &&
    citation.file_name
  ) {
    return `${citation.source_type}:${citation.file_name}`;
  }
  return citation.url || citation.title || '';
}
